import { Stack, Typography } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';

import VoiceAgentDemo from './VoiceAgentDemo';
import { type MomentId } from './VoiceAgentDemo.fixtures';

const step = (id: MomentId, title: string, note?: string) => ({
  id,
  title,
  render: () => <VoiceAgentDemo moment={id} />,
  note: note ? <Note variant='body2'>{note}</Note> : undefined,
});

/**
 * Jiameng's voice agent demo, rebuilt from one typed test call so there is
 * something to design the next step against while the hosted agent is down.
 */
export const voiceAgentDemoSpec: HandoverSpec = {
  id: 'voice-agent-demo',
  title: 'Voice agent demo',
  description:
    "Jiameng's onboarding voice agent as it stands, rebuilt from one test call on 27 October 2026.",
  screen: 'desktop',
  links: [
    { label: 'Hosted demo', url: 'https://voice-agent-demo.jiamenguk.workers.dev/livekit' },
    { label: 'Repo', url: 'https://github.com/sillwood/karehero-voice-agent' },
    { label: 'Questions doc', url: 'https://claude.ai/artifact/SXKZs6BS9JTeMugnAMp84H' },
  ],
  flows: [
    {
      title: 'Test call',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <Typography variant='body2'>
              A rebuild, not screenshots. The words are the transcript of one typed call, and the panel holds what the
              demo held at each point. Layout matches the demo; colours are ours.
            </Typography>
            <Typography variant='body2'>
              The flow is fixed in code: your situation, about them, when help is needed, contact details. The AI picks
              the wording; the code decides when a step is done.
            </Typography>
          </Stack>
        </Note>
      ),
      steps: [
        step('idle', 'Before the call'),
        step('greeting', 'Greeting', 'The only fixed line. Everything after it is generated.'),
        step('readBack', 'Read-back', 'Reads the situation back and waits for a yes before moving on.'),
        step(
          'realPerson',
          'Asked if it is a person',
          'Says it is an AI, then carries on. Marked disability as yes from the dementia mention, without asking.',
        ),
        step(
          'outsideUk',
          'Lives in Spain',
          'Saves "Outside the UK" and moves on. Nothing changes course, though the help asked for was money.',
        ),
        step(
          'correction',
          'Corrects mum to dad',
          'Says it has noted the change, but the record still says mum. A later step cannot update an earlier answer.',
        ),
        step('safeguarding', 'Raises a safeguarding worry', 'Asks about immediate danger. Nothing is flagged or recorded.'),
        step(
          'dropped',
          'Call drops',
          'The agent left after "No, not right now". Cause unknown. Answers become editable but are not saved.',
        ),
      ],
    },
  ],
  states: [],
  components: {},
};
